import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const SustainabilitySection = () => {
  const [activeInitiative, setActiveInitiative] = useState('sourcing');

  const sustainabilityInitiatives = {
    sourcing: {
      title: "Local & Sustainable Sourcing",
      description: "We prioritize partnerships with local farms and sustainable suppliers to reduce our environmental footprint while supporting our community\'s agricultural economy.",
      image: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      practices: [
        {
          name: "Local Farm Partnerships",
          description: "75% of our produce comes from farms within 50 miles",
          impact: "Reduced transportation emissions by 40%",
          icon: "Tractor"
        },
        {
          name: "Organic Ingredient Priority",
          description: "Prioritizing organic and pesticide-free ingredients",
          impact: "30% of menu items feature organic ingredients",
          icon: "Leaf"
        },
        {
          name: "Seasonal Menu Adaptation",
          description: "Menu changes based on seasonal ingredient availability",
          impact: "Supports local agriculture cycles",
          icon: "Calendar"
        },
        {
          name: "Sustainable Seafood",
          description: "All seafood sourced from certified sustainable fisheries",
          impact: "100% sustainable seafood commitment",
          icon: "Fish"
        }
      ]
    },
    waste: {
      title: "Waste Reduction & Recycling",
      description: "Our comprehensive waste management program focuses on reducing, reusing, and recycling to minimize our environmental impact while maintaining operational efficiency.",
      image: "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      practices: [
        {
          name: "Food Waste Minimization",
          description: "Precise portion control and inventory management",
          impact: "Reduced food waste by 60% since 2020",
          icon: "Trash2"
        },
        {
          name: "Composting Program",
          description: "All organic waste composted for local community gardens",
          impact: "500+ lbs of compost donated monthly",
          icon: "Recycle"
        },
        {
          name: "Packaging Optimization",
          description: "Biodegradable and recyclable packaging for takeout",
          impact: "Eliminated 90% of plastic packaging",
          icon: "Package"
        },
        {
          name: "Oil Recycling",
          description: "Used cooking oil converted to biodiesel fuel",
          impact: "100% cooking oil recycling rate",
          icon: "Droplets"
        }
      ]
    },
    energy: {
      title: "Energy Efficiency & Conservation",
      description: "We\'ve implemented energy-efficient systems and practices throughout our operations to reduce our carbon footprint while maintaining the quality our customers expect.",
      image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      practices: [
        {
          name: "LED Lighting System",
          description: "Complete transition to energy-efficient LED lighting",
          impact: "40% reduction in lighting energy consumption",
          icon: "Lightbulb"
        },
        {
          name: "Energy-Efficient Equipment",
          description: "ENERGY STAR certified kitchen and dining equipment",
          impact: "25% overall energy consumption reduction",
          icon: "Zap"
        },
        {
          name: "Smart Temperature Control",
          description: "Automated HVAC system with zone-based controls",
          impact: "30% reduction in heating/cooling costs",
          icon: "Thermometer"
        },
        {
          name: "Water Conservation",
          description: "Low-flow fixtures and water recycling systems",
          impact: "35% reduction in water usage",
          icon: "Droplets"
        }
      ]
    },
    community: {
      title: "Community Environmental Programs",
      description: "Beyond our restaurant operations, we actively participate in community environmental initiatives and educational programs to promote sustainability awareness.",
      image: "https://images.unsplash.com/photo-1559827260-dc66d52bef19?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      practices: [
        {
          name: "Community Garden Support",
          description: "Sponsoring and maintaining local community gardens",
          impact: "3 community gardens supported",
          icon: "Sprout"
        },
        {
          name: "Environmental Education",
          description: "Hosting workshops on sustainable cooking practices",
          impact: "200+ community members educated annually",
          icon: "BookOpen"
        },
        {
          name: "Tree Planting Initiative",
          description: "Annual tree planting events in local parks",
          impact: "150+ trees planted since 2019",
          icon: "TreePine"
        },
        {
          name: "Clean-Up Campaigns",
          description: "Monthly neighborhood and park clean-up events",
          impact: "12 clean-up events annually",
          icon: "Trash"
        }
      ]
    }
  };

  const initiatives = [
    { id: 'sourcing', name: 'Sustainable Sourcing', icon: 'Leaf' },
    { id: 'waste', name: 'Waste Reduction', icon: 'Recycle' },
    { id: 'energy', name: 'Energy Efficiency', icon: 'Zap' },
    { id: 'community', name: 'Community Programs', icon: 'Users' }
  ];

  const currentInitiative = sustainabilityInitiatives[activeInitiative];

  const certifications = [
    {
      name: "Green Business Certified",
      organization: "Green Business Alliance",
      year: "2021",
      description: "Certified for comprehensive environmental practices",
      icon: "Award"
    },
    {
      name: "Sustainable Restaurant Certification",
      organization: "Sustainable Restaurant Association",
      year: "2022",
      description: "Recognized for sustainable sourcing and operations",
      icon: "CheckCircle"
    },
    {
      name: "Local Food Champion",
      organization: "Farm to Table Coalition",
      year: "2023",
      description: "Honored for supporting local agriculture",
      icon: "Star"
    }
  ];

  return (
    <div className="py-8">
      <div className="text-center mb-16">
        <h2 className="font-headline text-4xl md:text-5xl font-bold text-primary mb-6">
          Sustainability & Environmental Responsibility
        </h2>
        <p className="font-body text-lg text-text-secondary max-w-3xl mx-auto">
          Our commitment to authentic Mediterranean cuisine extends to caring for the environment that provides our ingredients. We believe that sustainable practices are essential for preserving culinary traditions for future generations.
        </p>
      </div>

      {/* Initiative Navigation */}
      <div className="flex flex-wrap justify-center gap-4 mb-12">
        {initiatives.map((initiative) => (
          <button
            key={initiative.id}
            onClick={() => setActiveInitiative(initiative.id)}
            className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-cta font-semibold transition-organic ${
              activeInitiative === initiative.id
                ? 'bg-success text-white shadow-warm'
                : 'text-success hover:bg-success-50 border border-success-200'
            }`}
          >
            <Icon name={initiative.icon} size={20} />
            <span>{initiative.name}</span>
          </button>
        ))}
      </div>

      {/* Active Initiative Content */}
      <motion.div
        key={activeInitiative}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-16"
      >
        <div className="card-surface">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="font-headline text-3xl font-bold text-text-primary mb-4">
                {currentInitiative.title}
              </h3>
              <p className="font-body text-text-secondary leading-relaxed mb-6">
                {currentInitiative.description}
              </p>
              
              <div className="bg-success-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="Target" size={20} className="text-success-600" />
                  <span className="font-cta font-semibold text-success-700">
                    Our Impact Goals
                  </span>
                </div>
                <p className="text-success-800 text-sm">
                  Continuously improving our environmental practices while maintaining the authentic quality our customers expect.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="relative overflow-hidden rounded-lg shadow-warm-lg">
                <Image
                  src={currentInitiative.image}
                  alt={currentInitiative.title}
                  className="w-full h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-success-900/40 to-transparent"></div>
              </div>
            </div>
          </div>

          {/* Practices Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {currentInitiative.practices.map((practice, index) => (
              <motion.div
                key={practice.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-surface rounded-lg p-6 hover:shadow-warm transition-organic"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-success rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name={practice.icon} size={24} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-cta font-semibold text-text-primary mb-2">
                      {practice.name}
                    </h4>
                    <p className="font-body text-text-secondary text-sm mb-3 leading-relaxed">
                      {practice.description}
                    </p>
                    <div className="bg-success-50 rounded px-3 py-1">
                      <p className="text-success-700 text-xs font-medium">
                        Impact: {practice.impact}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Certifications */}
      <div className="mb-16">
        <h3 className="font-headline text-3xl font-bold text-text-primary text-center mb-12">
          Environmental Certifications
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {certifications.map((cert, index) => (
            <motion.div
              key={cert.name}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="card-surface text-center hover:shadow-warm-lg transition-organic"
            >
              <div className="w-16 h-16 bg-trust rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name={cert.icon} size={24} className="text-white" />
              </div>
              
              <h4 className="font-cta font-semibold text-text-primary mb-2">
                {cert.name}
              </h4>
              
              <p className="font-body text-trust font-medium mb-2">
                {cert.organization}
              </p>
              
              <span className="inline-block bg-trust-50 text-trust-700 px-3 py-1 rounded-full text-sm font-medium mb-3">
                {cert.year}
              </span>
              
              <p className="font-body text-text-secondary text-sm leading-relaxed">
                {cert.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Environmental Impact Summary */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="text-center"
      >
        <div className="max-w-4xl mx-auto card-surface bg-gradient-to-r from-success-50 to-trust-50">
          <Icon name="Globe" size={48} className="text-success mx-auto mb-6" />
          <h3 className="font-headline text-3xl font-bold text-text-primary mb-6">
            Our Environmental Commitment
          </h3>
          <p className="font-body text-lg text-text-secondary leading-relaxed mb-8">
            Sustainability isn't just a business practice for us – it's a responsibility to our community and future generations. By combining traditional Mediterranean values of respecting nature with modern environmental practices, we're creating a restaurant that nourishes both people and the planet.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="font-headline text-3xl font-bold text-success mb-2">40%</div>
              <p className="font-body text-text-secondary text-sm">Emission Reduction</p>
            </div>
            <div className="text-center">
              <div className="font-headline text-3xl font-bold text-trust mb-2">75%</div>
              <p className="font-body text-text-secondary text-sm">Local Sourcing</p>
            </div>
            <div className="text-center">
              <div className="font-headline text-3xl font-bold text-primary mb-2">60%</div>
              <p className="font-body text-text-secondary text-sm">Waste Reduction</p>
            </div>
            <div className="text-center">
              <div className="font-headline text-3xl font-bold text-accent mb-2">3</div>
              <p className="font-body text-text-secondary text-sm">Green Certifications</p>
            </div>
          </div>
          
          <div className="mt-8 pt-6 border-t border-success-200">
            <p className="font-accent italic text-text-secondary">
              "Caring for the earth is part of our Mediterranean heritage – it's how we ensure authentic flavors for generations to come."
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SustainabilitySection;